import type { QuizPlayersScoreData } from '@mentimeter/core-hooks';
import { useEffect, useMemo } from 'react';
import { ClientErrorCodes, GameStateEnums } from '@mentimeter/quiz-types';
import {
  useQuizMetaContext,
  useQuizResults,
  useQuizStatus,
  useSocketIoConnection,
} from '@mentimeter/quiz';
import { usePresentationContext } from '../../legacy-context/presentationContext';
import { Loading } from '../../shared/components/quiz/loading';
import { CenteringContainer } from '../../shared/components/quiz/CenteringContainer';
import { Error } from '../../shared/components/quiz/error';
import { FollowerPresentationDataHandler } from './FollowerPresentationDataHandler';
import { PresentersPresentationDataHandler } from './PresentersPresentationDataHandler';

export const LivePresentationDataHandler = () => {
  const { useCurrentSession } = usePresentationContext();
  const { currentSessionId: session } = useCurrentSession();
  const { data, mutate, error } = useQuizResults<QuizPlayersScoreData>();
  const { connection } = useSocketIoConnection();
  const { timeOffset } = useQuizStatus(connection);
  const { follower } = useQuizMetaContext();

  // We can have total scores, but we need to determine for this question
  const hasResultsForQuestion = useMemo(
    () => data && data.some((v) => v.score.question),
    [data],
  );

  // Invalidate results when session is updated
  useEffect(() => {
    return () => void mutate(undefined);
  }, [session, mutate]);

  if (
    !data ||
    !connection ||
    session === undefined ||
    timeOffset === undefined
  ) {
    return <Loading />;
  }

  if (error) {
    return (
      <CenteringContainer>
        <Error errorCode={ClientErrorCodes.RESULTS_FETCH} />
      </CenteringContainer>
    );
  }

  if (follower) {
    return <FollowerPresentationDataHandler activeSession={session} />;
  }

  return (
    // It is important that this component is rendered after the data has finished loading
    <PresentersPresentationDataHandler
      activeSession={session}
      initialGameState={
        hasResultsForQuestion ? GameStateEnums.RESULT : GameStateEnums.LOBBY
      }
    />
  );
};
